import React, { useContext, useState, useEffect } from "react";
import { Container, Card, Button, Dropdown, Spinner } from "react-bootstrap";
import { CurrentUserContext } from "../utils/CurrentUserContext";
import { useInbox, useUnreadCount, useMarkAsRead, useMarkAllAsRead, useDeleteMessage, useLoadMoreInbox } from "../../hooks/useInbox";
import { formatTimeAgo } from "../utils/timeUtils";

function NotificationListPage() {
    const [currentPage, setCurrentPage] = useState(1);
    const [allMessages, setAllMessages] = useState([]);
    const [hasMore, setHasMore] = useState(true);

    const { data: messagesData, isLoading, error } = useInbox(currentPage);
    const { data: unreadData } = useUnreadCount();
    const markAsRead = useMarkAsRead();
    const markAllAsRead = useMarkAllAsRead();
    const deleteMessage = useDeleteMessage();
    const loadMoreInbox = useLoadMoreInbox();
    const user = useContext(CurrentUserContext);

    const unreadCount = unreadData?.unread_count || 0;

    // Update allMessages when new page data comes in
    useEffect(() => {
        if (messagesData) {
            if (currentPage === 1) {
                // First page - replace all messages
                setAllMessages(messagesData.results || messagesData);
            } else {
                // Subsequent pages - append to existing messages
                setAllMessages(prev => [...prev, ...(messagesData.results || messagesData)]);
            }

            // Check if there are more pages
            if (messagesData.next) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }
        }
    }, [messagesData, currentPage]);

    const handleMarkAsRead = (messageId) => {
        markAsRead.mutate(messageId);
    };

    const handleMarkAllAsRead = () => {
        markAllAsRead.mutate();
    };

    const handleDeleteMessage = (messageId) => {
        deleteMessage.mutate(messageId);
    };

    const handleLoadMore = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        loadMoreInbox.mutate(nextPage);
    };

    const getAvatarInitials = (subject) => {
        // Filter out non-alphanumeric characters and spaces, then get initials
        const cleanSubject = subject.replace(/[^a-zA-Z0-9\s]/g, '');
        return cleanSubject.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
    };

    if (isLoading && currentPage === 1) {
        return (
            <Container className="p-0">
                <Card className="h-100">
                    <Card.Body className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (error && currentPage === 1) {
        return (
            <Container className="p-0">
                <Card className="h-100">
                    <Card.Body className="text-center">
                        <p className="text-danger">Failed to load notifications</p>
                        <p className="text-muted">{error.message}</p>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => window.location.reload()}
                        >
                            Retry
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="p-0">
            <Card className="h-100">
                {/* Card Header */}
                <Card.Header className="py-3 border-0 d-flex align-items-center justify-content-between">
                    <h1 className="h5 mb-0">Notifications</h1>
                    {/* Notification action START */}
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="text-secondary"
                            size="sm"
                            className="custom-dropdown-toggle"
                        >
                            <i className="bi bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={handleMarkAllAsRead}
                                disabled={markAllAsRead.isPending}
                            >
                                <i className="bi bi-check-lg fa-fw pe-2"></i>
                                {markAllAsRead.isPending ? 'Marking...' : 'Mark all read'}
                            </Dropdown.Item>
                            {/* <Dropdown.Item>
                                <i className="bi bi-bell-slash fa-fw pe-2"></i>Push notifications
                            </Dropdown.Item>
                            <Dropdown.Item>
                                <i className="bi bi-bell fa-fw pe-2"></i>Email notifications
                            </Dropdown.Item> */}
                        </Dropdown.Menu>
                    </Dropdown>
                    {/* Notification action END */}
                </Card.Header>

                <Card.Body className="p-2">
                    {!allMessages || allMessages.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="bi bi-inbox fs-1 text-muted"></i>
                            <p className="mt-3 text-muted">No notifications yet</p>
                        </div>
                    ) : (
                        <ul className="list-unstyled">
                            {allMessages.map((message) => (
                                <li key={message.id}>
                                    <div className={`rounded border-0 mb-1 ps-3 pe-2 p-2 position-relative ${!message.read_at ? 'badge-unread' : ''
                                        }`}>
                                        {/* Top row with avatar, title, and dropdown */}
                                        <div className="d-flex align-items-start justify-content-between mb-2">
                                            <div className="d-flex align-items-start">
                                                {/* Avatar */}
                                                <div className="avatar text-center me-3">
                                                    <div className="avatar-img rounded-circle bg-primary">
                                                        <span className="text-white position-absolute top-50 start-50 translate-middle fw-bold">
                                                            {getAvatarInitials(message.subject)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Title */}{/* Timestamp */}
                                                <div className="flex-grow-1">
                                                    <p className="small mb-0">
                                                        <strong>{message.subject}</strong><span className="text-muted ps-1"> {formatTimeAgo(message.created_at)}</span>
                                                    </p>
                                                    {/* Message body */}
                                                    <p className="small mb-0 text-muted">
                                                        {message.body.length > 100
                                                            ? `${message.body.substring(0, 100)}...`
                                                            : message.body
                                                        }
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Notification action START - Positioned in top right */}
                                            <Dropdown>
                                                <Dropdown.Toggle
                                                    variant="text-secondary"
                                                    size="sm"
                                                    className="custom-dropdown-toggle"
                                                >
                                                    <i className="bi bi-three-dots"></i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    {!message.read_at && (
                                                        <Dropdown.Item
                                                            onClick={() => handleMarkAsRead(message.id)}
                                                            disabled={markAsRead.isPending}
                                                        >
                                                            <i className="bi bi-check-lg fa-fw pe-2"></i>
                                                            {markAsRead.isPending ? 'Marking...' : 'Mark as read'}
                                                        </Dropdown.Item>
                                                    )}
                                                    <Dropdown.Item
                                                        onClick={() => handleDeleteMessage(message.id)}
                                                        disabled={deleteMessage.isPending}
                                                    >
                                                        <i className="bi bi-trash fa-fw pe-2"></i>
                                                        {deleteMessage.isPending ? 'Deleting...' : 'Delete'}
                                                    </Dropdown.Item>
                                                    {/* <Dropdown.Item>
                                                        <i className="bi bi-bell-slash fa-fw pe-2"></i>Turn off
                                                    </Dropdown.Item> */}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            {/* Notification action END */}
                                        </div>

                                        {/* Bottom row with message body and timestamp */}
                                        <div className="d-flex align-items-start justify-content-between">


                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card.Body>

                {hasMore && allMessages.length > 0 && (
                    <Card.Footer className="border-0 py-3 text-center position-relative d-grid pt-0">
                        {/* Load more button START */}
                        <Button
                            variant="primary-soft"
                            className="btn-loader"
                            onClick={handleLoadMore}
                            disabled={loadMoreInbox.isPending}
                        >
                            <span className="load-text">
                                {loadMoreInbox.isPending ? 'Loading...' : 'Load more notifications'}
                            </span>
                            {loadMoreInbox.isPending && (
                                <div className="load-icon">
                                    <div className="spinner-grow spinner-grow-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                        </Button>
                        {/* Load more button END */}
                    </Card.Footer>
                )}
            </Card>
        </Container>
    );
}

export default NotificationListPage;
